import { OVERLAY_ALIGN_OPTIONS, CONTAINER_ALIGN_INSET } from "./overlayAlign";
import { resolveElementLayoutScale } from "./dragAlignment";
import { resolveTextStylePreset } from "./globalStylePresets";
import { buildVisualEffectsStyle } from "./visualEffects";

export const TEXT_ANIMATION_TYPES = Object.freeze([
  "none",
  "fade",
  "slide-up",
  "slide-left",
  "slide-right",
]);
export const TEXT_ANIMATION_DURATION_MIN = 100;
export const TEXT_ANIMATION_DURATION_MAX = 10000;
export const TEXT_ANIMATION_DELAY_MIN = 0;
export const TEXT_ANIMATION_DELAY_MAX = 10000;
export const DEFAULT_TEXT_ANIMATION_DURATION = 600;
export const TEXT_FONT_SIZE_MIN_PX = 1;
export const TEXT_FONT_SIZE_MAX_PX = 1000;
export const TEXT_BACKGROUND_PADDING_MIN_PX = 0;
export const TEXT_BACKGROUND_PADDING_MAX_PX = 200;
export const TEXT_BACKGROUND_RADIUS_MIN_PX = 0;
export const TEXT_BACKGROUND_RADIUS_MAX_PX = 1000;

const TEXT_ANIMATION_TYPE_SET = new Set(TEXT_ANIMATION_TYPES);
export const TEXT_ANIMATION_KEYFRAMES = Object.freeze({
  fade: "web-maker-text-fade",
  "slide-up": "web-maker-text-slide-up",
  "slide-left": "web-maker-text-slide-left",
  "slide-right": "web-maker-text-slide-right",
});

const clampAnimationTime = (value, fallback, min, max) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.round(Math.min(max, Math.max(min, numeric)));
};

export const parseFontSizePx = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const match = String(value).trim().match(/^(\d+(?:\.\d+)?)\s*(?:px)?$/i);
  if (!match) return null;
  const numeric = Number(match[1]);
  return Number.isFinite(numeric) ? numeric : null;
};

export const normalizeFontSizePx = (value, fallback = "") => {
  const numeric = parseFontSizePx(value);
  if (numeric === null) return fallback;
  const clamped = Math.min(
    TEXT_FONT_SIZE_MAX_PX,
    Math.max(TEXT_FONT_SIZE_MIN_PX, numeric)
  );
  const rounded = Math.round(clamped * 100) / 100;
  return `${rounded}px`;
};

export const getFontSizeInputValue = (value) => {
  const numeric = parseFontSizePx(value);
  return numeric === null ? "" : numeric;
};

const parseTextBoxDimensionPx = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const match = String(value).trim().match(/^(-?\d+(?:\.\d+)?)\s*(?:px)?$/i);
  if (!match) return null;
  const numeric = Number(match[1]);
  return Number.isFinite(numeric) ? numeric : null;
};

export const normalizeTextBoxDimensionPx = (
  value,
  { max = TEXT_BACKGROUND_RADIUS_MAX_PX, fallback = "" } = {}
) => {
  const numeric = parseTextBoxDimensionPx(value);
  if (numeric === null) return fallback;
  const rounded =
    Math.round(Math.min(max, Math.max(0, numeric)) * 100) / 100;
  return `${rounded}px`;
};

export const getTextBoxDimensionInputValue = (value, max) => {
  const normalized = normalizeTextBoxDimensionPx(value, { max });
  return normalized ? Number(normalized.replace(/px$/i, "")) : "";
};

export const createDefaultTextStyle = () => ({
  color: "",
  backgroundColor: "",
  padding: "",
  borderRadius: "",
  fontFamily: "",
  fontWeight: "normal",
  fontStyle: "normal",
  textDecoration: "none",
  fontSize: "",
  lineHeight: "",
  letterSpacing: "",
  textTransform: "none",
  semanticRole: "",
  stylePreset: "",
  align: "",
  offsetX: 0,
  offsetY: 0,
  layoutReserve: 0,
  width: "",
  minHeight: "",
  linkUrl: "",
  linkTarget: "_self",
  linkRel: "",
  animationType: "none",
  animationDuration: DEFAULT_TEXT_ANIMATION_DURATION,
  animationDelay: 0,
});

const toOffset = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const normalizeTextStyle = (style, legacyColor) => {
  const base = createDefaultTextStyle();
  if (style && typeof style === "object") {
    return {
      ...base,
      ...style,
      color: style.color || legacyColor || "",
      padding: normalizeTextBoxDimensionPx(style.padding, {
        max: TEXT_BACKGROUND_PADDING_MAX_PX,
      }),
      borderRadius: normalizeTextBoxDimensionPx(style.borderRadius, {
        max: TEXT_BACKGROUND_RADIUS_MAX_PX,
      }),
      offsetX: toOffset(style.offsetX),
      offsetY: toOffset(style.offsetY),
      layoutReserve: toOffset(style.layoutReserve),
      animationType: TEXT_ANIMATION_TYPE_SET.has(style.animationType)
        ? style.animationType
        : "none",
      animationDuration: clampAnimationTime(
        style.animationDuration,
        DEFAULT_TEXT_ANIMATION_DURATION,
        TEXT_ANIMATION_DURATION_MIN,
        TEXT_ANIMATION_DURATION_MAX
      ),
      animationDelay: clampAnimationTime(
        style.animationDelay,
        0,
        TEXT_ANIMATION_DELAY_MIN,
        TEXT_ANIMATION_DELAY_MAX
      ),
    };
  }
  if (legacyColor) {
    return { ...base, color: legacyColor };
  }
  return base;
};

export const buildComputedTextStyle = (
  style,
  defaults = {},
  siteFontFamily = "sans-serif",
  globalStylePresets = {}
) => {
  const normalized = normalizeTextStyle(
    resolveTextStylePreset(style, globalStylePresets)
  );
  const color = normalized.color || defaults.color;
  const fontFamily = normalized.fontFamily || defaults.fontFamily || siteFontFamily;
  const computed = {
    fontFamily,
    fontWeight: normalized.fontWeight === "bold" ? "bold" : "normal",
    fontStyle: normalized.fontStyle === "italic" ? "italic" : "normal",
    textDecoration: normalized.textDecoration === "underline" ? "underline" : "none",
  };

  if (color) {
    computed.color = color;
  }
  if (normalized.backgroundColor) {
    computed.backgroundColor = normalized.backgroundColor;
    if (normalized.padding) computed.padding = normalized.padding;
    if (normalized.borderRadius) {
      computed.borderRadius = normalized.borderRadius;
    }
    if (normalized.padding || normalized.borderRadius) {
      computed.boxSizing = "border-box";
    }
  }
  Object.assign(
    computed,
    buildVisualEffectsStyle(normalized.visualEffects, {
      legacyBoxShadow: normalized.boxShadow,
    })
  );
  if (normalized.fontSize) {
    computed.fontSize = normalizeFontSizePx(
      normalized.fontSize,
      normalized.fontSize
    );
  }
  if (normalized.lineHeight !== "" && normalized.lineHeight !== undefined) {
    const numericLineHeight = Number(normalized.lineHeight);
    computed.lineHeight = Number.isFinite(numericLineHeight)
      ? Math.min(4, Math.max(0.5, numericLineHeight))
      : normalized.lineHeight;
  }
  if (normalized.letterSpacing !== "" && normalized.letterSpacing !== undefined) {
    computed.letterSpacing =
      typeof normalized.letterSpacing === "number"
        ? `${normalized.letterSpacing}px`
        : normalized.letterSpacing;
  }
  if (["none", "uppercase", "lowercase", "capitalize"].includes(normalized.textTransform)) {
    computed.textTransform = normalized.textTransform;
  }
  if (normalized.align === "justify") {
    computed.textAlign = "justify";
  }

  const animationName = TEXT_ANIMATION_KEYFRAMES[normalized.animationType];
  if (animationName) {
    computed.animationName = animationName;
    computed.animationDuration = `${normalized.animationDuration}ms`;
    computed.animationDelay = `${normalized.animationDelay}ms`;
    computed.animationTimingFunction = "ease-out";
    computed.animationIterationCount = 1;
    computed.animationFillMode = "both";
  }

  return computed;
};

export const TEXT_ALIGN_SCOPE_CLASS = "web-maker-text-align-scope";

const buildCanvasRelativeLeft = (offsetX, canvasWidth) => {
  const width = Number(canvasWidth);
  if (!Number.isFinite(width) || width <= 0) return offsetX;

  const halfWidth = width / 2;
  const operator = offsetX < 0 ? "-" : "+";
  return `calc(max(0px, 50% - ${halfWidth}px) ${operator} ${Math.abs(offsetX)}px)`;
};

export const buildPageAlignFieldStyle = (style, { canvasWidth } = {}) => {
  const normalized = normalizeTextStyle(style);
  const { align, offsetX, offsetY } = normalized;
  const hasOffset = offsetX !== 0 || offsetY !== 0;
  if (!align && !hasOffset) return null;

  if (align === OVERLAY_ALIGN_OPTIONS.JUSTIFY) {
    return {
      position: "absolute",
      left: CONTAINER_ALIGN_INSET,
      right: CONTAINER_ALIGN_INSET,
      top: offsetY,
      width: "auto",
      zIndex: 2,
    };
  }

  return {
    position: "absolute",
    left: buildCanvasRelativeLeft(offsetX, canvasWidth),
    top: offsetY,
    width: "max-content",
    maxWidth: `calc(100% - ${CONTAINER_ALIGN_INSET * 2}px)`,
    zIndex: 2,
  };
};

export const resolveScopeRelativeOffset = (fieldEl, offsetX = 0, offsetY = 0) => {
  if (!fieldEl) {
    return { offsetX, offsetY };
  }

  const scope = fieldEl.closest(`.${TEXT_ALIGN_SCOPE_CLASS}`);
  if (!scope) {
    return { offsetX, offsetY };
  }

  const fieldRect = fieldEl.getBoundingClientRect();
  const scopeRect = scope.getBoundingClientRect();

  return {
    offsetX: Math.round(fieldRect.left - scopeRect.left),
    offsetY: Math.round(fieldRect.top - scopeRect.top),
  };
};

/**
 * Snapshot a text field in the coordinate system CSS will actually use after
 * it switches from normal/flex flow to position:absolute. The previous drag
 * path measured against the nearest visual alignment scope, which may differ
 * from offsetParent (for example a CTA row inside a hero scope), causing the
 * first drag frame to jump by hundreds of pixels and collapse flex width.
 */
export const resolveTextDragStartGeometry = (
  fieldEl,
  { offsetX = 0, offsetY = 0, canvasWidth } = {}
) => {
  if (!fieldEl) {
    return {
      offsetX,
      offsetY,
      width: "",
      height: 0,
      scaleX: 1,
      scaleY: 1,
    };
  }

  const parent = fieldEl.offsetParent;
  if (!parent) {
    const fallback = resolveScopeRelativeOffset(fieldEl, offsetX, offsetY);
    return {
      ...fallback,
      width: fieldEl.offsetWidth || "",
      height: fieldEl.offsetHeight || 0,
      scaleX: 1,
      scaleY: 1,
    };
  }

  const parentWidth = Number(parent.clientWidth) || 0;
  const authoredCanvasWidth = Number(canvasWidth);
  const centeredCanvasInset =
    Number.isFinite(authoredCanvasWidth) && authoredCanvasWidth > 0
      ? Math.max(0, (parentWidth - authoredCanvasWidth) / 2)
      : 0;
  const { scaleX, scaleY } = resolveElementLayoutScale(fieldEl);

  return {
    offsetX: Math.round((Number(fieldEl.offsetLeft) || 0) - centeredCanvasInset),
    offsetY: Math.round(Number(fieldEl.offsetTop) || 0),
    width: Math.max(0, Math.round(Number(fieldEl.offsetWidth) || 0)) || "",
    height: Math.max(0, Math.round(Number(fieldEl.offsetHeight) || 0)),
    scaleX,
    scaleY,
  };
};

export const computeAlignOffsetX = (align, fieldEl) => {
  if (!fieldEl || !align || align === OVERLAY_ALIGN_OPTIONS.JUSTIFY) {
    return CONTAINER_ALIGN_INSET;
  }

  const scope = fieldEl.closest(`.${TEXT_ALIGN_SCOPE_CLASS}`);
  if (!scope) {
    return CONTAINER_ALIGN_INSET;
  }

  const fieldWidth = fieldEl.offsetWidth;
  const scopeWidth = scope.clientWidth;

  if (align === OVERLAY_ALIGN_OPTIONS.RIGHT) {
    return Math.max(CONTAINER_ALIGN_INSET, scopeWidth - fieldWidth - CONTAINER_ALIGN_INSET);
  }

  if (align === OVERLAY_ALIGN_OPTIONS.CENTER) {
    return Math.max(0, Math.round((scopeWidth - fieldWidth) / 2));
  }

  return CONTAINER_ALIGN_INSET;
};

export const hasTextPositionOffset = (style) => {
  const normalized = normalizeTextStyle(style);
  return normalized.offsetX !== 0 || normalized.offsetY !== 0;
};

export const resolvePickerColor = (style, fallback) => style?.color || fallback || "#000000";

export const resolvePickerBackgroundColor = (style) => style?.backgroundColor || "#ffffff";
